import { isNil } from 'lodash-es';
import { Resource } from '../Resource';
import * as methods from '../methods';
const rootPath = 'oad/cumulative_sum';
/** list of metric that are displayed as a cumul(area) in chart  */
export const cumulativeSumMetricsDisplayedAsArea = [
    'heatAccumulation',
    'RRAccumulation',
    'SSI_HOURSAccumulation',
    'COLD_T_HOUR'
];
/** Formats of the metric the back-end expects */
export const cumulativeSumMetrics = ['T', 'RR', 'LW_D', 'COLD_T_HOUR', 'SSI_HOURS', 'etp'];
/** Formats of the metrics we expect */
export const accumulationMetrics = [
    'heatAccumulation',
    'RRAccumulation',
    'SSI_HOURSAccumulation',
    'COLD_T_HOUR',
    'etp',
    'LW_D'
];
export const isCumulativeSumMetric = (value) => {
    return cumulativeSumMetrics.includes(value);
};
export function toFrontEndAccumulationMetric(metric) {
    switch (metric) {
        case 'T':
            return 'heatAccumulation';
        case 'RR':
            return 'RRAccumulation';
        case 'SSI_HOURS':
            return 'SSI_HOURSAccumulation';
        case 'LW_D':
        case 'COLD_T_HOUR':
        case 'etp':
            return metric;
    }
}
export function toBackEndCumulativeSumMetric(metric) {
    switch (metric) {
        case 'heatAccumulation':
            return 'T';
        case 'RRAccumulation':
            return 'RR';
        case 'SSI_HOURSAccumulation':
            return 'SSI_HOURS';
        case 'LW_D':
        case 'COLD_T_HOUR':
        case 'etp':
            return metric;
    }
}
const fromRaw = {
    history: (raw, metric) => metric !== 'T'
        ? {
            cumulativeSum: raw.data[`${metric}_cumsum`] ?? {},
            referenceCumulativeSum: {}
        }
        : {
            cumulativeSum: raw.data.degree_day_cumsum ?? {},
            referenceCumulativeSum: raw.data.degree_day_ref_cumsum ?? {}
        },
    config: (raw) => ({
        base: raw.base,
        plotId: raw.plot,
        stationId: raw.station,
        timestamps: raw.timestamps,
        activated: raw.activated,
        coldTemperatureThreshold: raw.cold_temperature_threshold
    }),
    results: (raw) => ({
        missingDays: raw.missing_days,
        plotId: raw.plot,
        stationId: raw.station,
        sums: raw.sums
    })
};
const toRaw = {
    config: (form) => {
        const payload = {
            base: form.base,
            timestamps: form.timestamps,
            activated: form.activated,
            cold_temperature_threshold: form.coldTemperatureThreshold
        };
        if (!isNil(form.plotId)) {
            payload.plot = form.plotId;
        }
        else if (!isNil(form.stationId)) {
            payload.station = form.stationId;
        }
        return payload;
    }
};
const cumulativeSum = Resource((requesterBuilder) => {
    const result = (params) => methods.get({
        rootPath: `${rootPath}/result`,
        requesterBuilder,
        fromRaw: fromRaw.results,
        params: {
            query: {
                since: params.since,
                base: params.base,
                before: params.before,
                span: params.span,
                station: params.stationId,
                plot: params.plotId,
                device: params.deviceId,
                cumul_types: params.metrics?.map(toBackEndCumulativeSumMetric)
            }
        }
    })();
    return {
        history: (query) => requesterBuilder({
            path: `${rootPath}/history`,
            requestCtx: {
                params: {
                    query: {
                        plot: query?.plotId,
                        station: query?.stationId,
                        device: query?.deviceId,
                        since: query?.since,
                        before: query?.before,
                        gdd_t_max: query?.metric === 'heatAccumulation' ? query?.gdd_t_max : undefined,
                        base: (query?.metric ?? 'T') === 'T' ||
                            query?.metric === 'COLD_T_HOUR' ||
                            query?.metric === 'heatAccumulation'
                            ? query?.base
                            : undefined,
                        cumul_types: query?.metric ? toBackEndCumulativeSumMetric(query?.metric) : undefined
                    }
                },
                transform: (raw) => fromRaw.history(raw, query?.metric ? toBackEndCumulativeSumMetric(query?.metric) : 'T')
            }
        }),
        setConfig: methods.create({
            rootPath: `${rootPath}/config`,
            requesterBuilder,
            fromRaw: fromRaw.config,
            toRaw: toRaw.config,
            buildKeysToInvalidate: () => [result({}).invalidationKey]
        }),
        getConfig: (params) => methods.get({
            rootPath: `${rootPath}/config`,
            requesterBuilder,
            fromRaw: fromRaw.config,
            params: { query: { plot: params.plotId, station: params.stationId } }
        })(),
        result
    };
});
export default cumulativeSum;
